<template>
  <div class="liveness-wrapper">
    <div id="biometria-widget-container" v-show="!carregando && !erroCarregamento"></div>

    <!-- Feedback de Carregamento -->
    <div v-if="carregando" class="feedback-container">
      <div class="spinner"></div>
      <p class="feedback-texto">Carregando sistema de validação documental...</p>
    </div>

    <!-- Feedback de Erro com Ação de Recuperação -->
    <div v-if="erroCarregamento" class="feedback-container erro">
      <div class="icone-erro">⚠️</div>
      <h3 class="erro-titulo">Não foi possível iniciar a validação</h3>
      <p class="feedback-texto">{{ erroCarregamento }}</p>
      <button type="button" class="btn-tentar-novamente" @click="inicializarSdk">
        Tentar Novamente
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

defineProps({
  link_ajuda: String,
})

const emit = defineEmits(['concluido'])

const urlAmbSDK = process.env.VUE_APP_NODE_ENV === 'production'
  ? "https://idser.leiloesjudiciais.com.br"
  : "https://idser.serranoleiloes.com.br";

const scriptUrl = `${urlAmbSDK}/sdk/liveness-sdk.js`;

const carregando = ref(true)
const erroCarregamento = ref(null)

function carregarScript(url) {
  return new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && typeof window.LivenessSDK !== 'undefined' && typeof window.LivenessSDK.init === 'function') {
      return resolve(window.LivenessSDK);
    }

    const scriptExistente = document.querySelector(`script[src="${url}"]`);
    if (scriptExistente) {
      if (typeof window.LivenessSDK !== 'undefined' && typeof window.LivenessSDK.init === 'function') {
        return resolve(window.LivenessSDK);
      }
      scriptExistente.remove();
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = true;

    const timeout = setTimeout(() => {
      script.remove();
      reject(new Error('Tempo limite excedido ao carregar o módulo de validação biométrica.'));
    }, 15000);

    script.onload = () => {
      clearTimeout(timeout);
      if (typeof window.LivenessSDK !== 'undefined' && typeof window.LivenessSDK.init === 'function') {
        resolve(window.LivenessSDK);
      } else {
        script.remove();
        reject(new Error('LivenessSDK carregado porém objeto não encontrado.'));
      }
    };

    script.onerror = () => {
      clearTimeout(timeout);
      script.remove();
      reject(new Error(`Não foi possível conectar ao servidor de validação biométrica (${url}).`));
    };

    document.head.appendChild(script);
  });
}

async function inicializarSdk() {
  carregando.value = true;
  erroCarregamento.value = null;

  try {
    await carregarScript(scriptUrl);

    // Guarda estrita antes de chamar init
    if (typeof window.LivenessSDK === 'undefined' || typeof window.LivenessSDK.init !== 'function') {
      throw new Error('O módulo LivenessSDK não pôde ser inicializado.');
    }

    window.LivenessSDK.init({
      containerId: 'biometria-widget-container',
      apiUrl: urlAmbSDK,
      onSuccess: function (result) {
        console.log("Identidade Validada! Dados OCR extraídos:", result.document_data)
        localStorage.setItem('biometria', JSON.stringify(result))

        // Salva dados mínimos compatíveis em apiResponse para o backend
        if (result && result.document_data) {
          const apiResponse = {
            data: [
              {
                extraction: {
                  person: {
                    name: result.document_data.nome,
                    taxId: result.document_data.cpf
                  }
                }
              }
            ]
          };
          localStorage.setItem('apiResponse', JSON.stringify(apiResponse));
        }

        // Mapeia chaves temporárias para o formato esperado pelo backend
        if (result && (result.selfie_s3_key || result.document_s3_key)) {
          const s3_keys = {
            selfie: result.selfie_s3_key || '',
            documento: result.document_s3_key || ''
          };
          localStorage.setItem('nxt_s3_keys', JSON.stringify(s3_keys));
        }

        // Emite o resultado para o componente pai tratar os dados
        emit('concluido', {
          biometria: result || {}
        })
      }
    });

    carregando.value = false;
  } catch (err) {
    console.error("Falha na inicialização do LivenessSDK:", err);
    carregando.value = false;
    erroCarregamento.value = err.message || 'Falha ao carregar o módulo de validação de documentos.';
  }
}

onMounted(() => {
  inicializarSdk();
})
</script>

<style scoped>
.liveness-wrapper {
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

#biometria-widget-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
}

.feedback-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  text-align: center;
  z-index: 10;
  max-width: 480px;
}

.spinner {
  width: 44px;
  height: 44px;
  border: 4px solid #e0e0e0;
  border-top-color: #032c35;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.icone-erro {
  font-size: 40px;
  margin-bottom: 12px;
}

.erro-titulo {
  font-size: 1.25rem;
  font-weight: 600;
  color: #c53030;
  margin-bottom: 8px;
}

.feedback-texto {
  font-size: 0.95rem;
  color: #4a5568;
  margin-bottom: 16px;
  line-height: 1.5;
}

.btn-tentar-novamente {
  background-color: #032c35;
  color: #ffffff;
  border: none;
  padding: 10px 24px;
  font-size: 0.95rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.btn-tentar-novamente:hover {
  background-color: #054a59;
}
</style>